import React from 'react';
import { TickerDropdown } from '../../components/common/TickerDropdown';
import { IntradaySignalUpdate } from '../../types/trading';
import styles from '../IntradaySignalTestPage.module.css';
import { CapControls, CapResults } from './CapSection';
import { MaControls, MaResults } from './MaSection';
import { ScheduleControls, ScheduleResults } from './ScheduleSection';
import { SignalChart } from './SignalChart';
import { SIGNAL_MODE_ORDER, SIGNAL_REGISTRY, SignalMode } from './signalConfig';
import { cardLabel, StrategyCardState } from './strategyCards';

interface StrategyCardProps {
  card: StrategyCardState;
  index: number;
  update: IntradaySignalUpdate | null;
  chartResetToken: number;
  removable: boolean;
  configError: string | null;
  onChange: (card: StrategyCardState) => void;
  onRemove: () => void;
}

export const StrategyCard: React.FC<StrategyCardProps> = ({
  card,
  index,
  update,
  chartResetToken,
  removable,
  configError,
  onChange,
  onRemove,
}) => {
  const meta = SIGNAL_REGISTRY[card.mode];
  const shortWindow = Number(card.maForm.shortWindowRaw) || 0;
  const longWindow = Number(card.maForm.longWindowRaw) || 0;
  const triggered = Boolean(update?.triggered);

  const handleModeChange = (mode: SignalMode) => {
    if (mode === card.mode) return;
    onChange({ ...card, mode });
  };

  return (
    <section className={styles.strategyCard} data-testid={`intraday-card-${index}`}>
      <header className={styles.strategyCardHeader}>
        <button
          type="button"
          className={styles.collapseButton}
          onClick={() => onChange({ ...card, collapsed: !card.collapsed })}
          data-testid={`intraday-card-collapse-${index}`}
        >
          {card.collapsed ? '▸' : '▾'}
        </button>
        <h2 className={styles.strategyCardTitle}>{cardLabel(card, index)}</h2>
        <span
          className={`${styles.cardStatus} ${triggered ? styles.cardStatusFired : ''}`.trim()}
          data-testid={`intraday-card-status-${index}`}
        >
          {update?.sample_status ?? 'idle'}
        </span>
        {removable ? (
          <button
            type="button"
            className={styles.removeButton}
            onClick={onRemove}
            data-testid={`intraday-card-remove-${index}`}
            aria-label={`Remove strategy ${index + 1}`}
          >
            ×
          </button>
        ) : null}
      </header>

      {card.collapsed ? null : (
        <>
          <p className={styles.subTitle}>{meta.description}</p>

          <div className={styles.controls}>
            <label className={styles.control}>
              <span>Stock</span>
              <TickerDropdown
                value={card.symbol}
                onChange={(symbol) => onChange({ ...card, symbol })}
                placeholder="Select symbol"
                testId={`intraday-symbol-${index}`}
              />
            </label>

            <label className={styles.control}>
              <span>Signal</span>
              <select
                value={card.mode}
                onChange={(e) => handleModeChange(e.target.value as SignalMode)}
                className={styles.select}
                data-testid={`intraday-signal-select-${index}`}
              >
                {SIGNAL_MODE_ORDER.map((mode) => (
                  <option key={mode} value={mode}>
                    {SIGNAL_REGISTRY[mode].label}
                  </option>
                ))}
              </select>
            </label>

            {card.mode === 'ma' ? (
              <MaControls form={card.maForm} onChange={(maForm) => onChange({ ...card, maForm })} />
            ) : null}
            {card.mode === 'percent_cap' ? (
              <CapControls
                mode="percent_cap"
                form={card.percentCapForm}
                onChange={(percentCapForm) => onChange({ ...card, percentCapForm })}
              />
            ) : null}
            {card.mode === 'dollar_cap' ? (
              <CapControls
                mode="dollar_cap"
                form={card.dollarCapForm}
                onChange={(dollarCapForm) => onChange({ ...card, dollarCapForm })}
              />
            ) : null}
            {card.mode === 'on_schedule' ? (
              <ScheduleControls
                form={card.scheduleForm}
                onChange={(scheduleForm) => onChange({ ...card, scheduleForm })}
              />
            ) : null}
          </div>

          {configError ? (
            <p className={styles.cardError} data-testid={`intraday-card-error-${index}`}>
              ⚠ {configError}
            </p>
          ) : null}

          {card.mode === 'ma' ? (
            <MaResults update={update} shortWindow={shortWindow} longWindow={longWindow} />
          ) : card.mode === 'on_schedule' ? (
            <ScheduleResults update={update} />
          ) : (
            <CapResults mode={card.mode} update={update} />
          )}

          <div className={styles.statusRow}>
            <span>Skipped Invalid Mid: {update?.skipped_invalid_mid ?? 0}</span>
            {card.mode === 'ma' ? (
              <span>Warmup: {update?.warmup_complete ? 'complete' : 'pending'}</span>
            ) : null}
            <span data-testid={`intraday-card-trigger-${index}`}>
              {meta.label} Triggered: {triggered ? 'True' : 'False'}
            </span>
          </div>

          <SignalChart
            mode={card.mode}
            update={update}
            resetToken={chartResetToken}
            shortWindow={shortWindow}
            longWindow={longWindow}
          />
        </>
      )}
    </section>
  );
};
